import {Folio, FolioStatus} from "crqlar-react-common/dist/models/billing/folio.model";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {getErrorMessage} from "crqlar-react-common/dist/utils/http.util";
import {enqueueSnackbar} from "notistack";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {DigitalSigningCreateOptions, DigitalSigningCreateResponse, DigitalSigningPrefill} from "../digitalSigning.model";
import {DigitalSigningService, isDigitalSigningError} from "../digitalSigning.service";
import {digitalSigningFolioKey, digitalSigningPrefillKey, invalidateDigitalSigningTemplateChoices} from "../digitalSigningQueries";
import {DigitalSigningStatus} from "../public/digitalSigning.types";
import {DigitalSigningTemplateInstantiateRequest, STARTER_KIND_BY_SECTOR_TYPE} from "../settings/digitalSigningTemplate.model";
import {DigitalSigningTemplateService} from "../settings/digitalSigningTemplate.service";
import {useDigitalSigningOffered} from "../settings/useDigitalSigningOffered";
import {usePageVisible} from "./digitalSigningFlow.hooks";
import {
    DigitalSigningFlowState,
    digitalSigningFlowState,
    digitalSigningLinesIssue,
    digitalSigningPollInterval,
    isSignedDigitalSigning
} from "./digitalSigningFlow.util";

const KEPT_STATUSES: DigitalSigningStatus[] = ['PENDING', 'OPENED', 'SIGNED', 'CONFIRMED'];
const LINK_STATES: DigitalSigningFlowState['kind'][] = ['DRAFT', 'LIVE', 'LIVE_WITHOUT_TOKEN'];
const HIDDEN: DigitalSigningFlowState = {kind: 'HIDDEN'};

interface Options {
    folio?: Folio;
    reservation?: Reservation;
    items?: FolioItem[];
    open: boolean;
    readOnly: boolean;
    beforeCreate?: () => Promise<boolean>;
    onConfirmed: (folio: Folio) => void;
    onBillToChanged?: () => void;
}

export const useDigitalSigning = ({folio, reservation, items, open, readOnly, beforeCreate, onConfirmed, onBillToChanged}: Options) => {
    const {t, i18n} = useTranslation();
    const queryClient = useQueryClient();
    const pageVisible = usePageVisible();
    const folioId = folio?.id;
    const sectorId = reservation?.sectorId;
    const isDraft = folio?.status === FolioStatus.DRAFT;
    const offered = useDigitalSigningOffered(sectorId);
    const sectorType = useAllSectors().find(sector => sector.id === sectorId)?.sectorType;
    const starterKind = sectorType && STARTER_KIND_BY_SECTOR_TYPE[sectorType];
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    // The footer's main button is at work: its spinner
    const [creatingRightAway, setCreatingRightAway] = useState(false);
    const [staleId, setStaleId] = useState<string>();
    const [templateId, setTemplateId] = useState<string>();
    const [profileId, setProfileId] = useState<string>();
    const [tipStaffResourceId, setTipStaffResourceId] = useState<string>();
    const [room, setRoom] = useState<string>();
    const [language, setLanguage] = useState<string>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const query = useQuery(
        digitalSigningFolioKey(folioId),
        () => DigitalSigningService.latestForFolio(folioId!),
        {
            enabled: open && !!folioId && !!reservation?.id,
            refetchOnWindowFocus: false,
            refetchInterval: data => digitalSigningPollInterval(isDraft ? data?.status : undefined, open, pageVisible),
        }
    );
    const latest = query.data;
    const loading = query.isLoading || query.isIdle;
    const loadFailed = query.isError && !latest;
    const kept = !!latest?.status && KEPT_STATUSES.includes(latest.status);
    const available = !readOnly && !!folioId && !!reservation?.id && (!!offered || !!starterKind || kept);

    const token = DigitalSigningService.issuedToken(latest?.id);
    const state = available ? digitalSigningFlowState({
        latest,
        tokenKnown: !!token,
        folioStatus: folio?.status,
        linesIssue: items && digitalSigningLinesIssue(items),
    }) : HIDDEN;
    const stale = !!latest && latest.id === staleId && latest.status === 'SIGNED';

    const prefillEnabled = open && state.kind === 'DRAFT' && !state.blockedBy;
    const prefillQuery = useQuery(
        digitalSigningPrefillKey(folioId, reservation?.id),
        () => DigitalSigningService.prefill(folioId!, reservation?.id),
        {
            enabled: prefillEnabled,
            staleTime: 60 * 1000,
            refetchOnWindowFocus: cached => (cached.state.data?.templates.length === 0 ? 'always' : false),
        }
    );
    const prefill = prefillQuery.data;
    // The options for a prefill: staff's picks where they made one, else its defaults - what the dialog
    // shows, and what a link is created with
    const choicesOf = (read?: DigitalSigningPrefill) => {
        const templates = read?.templates ?? [];
        const persons = read?.persons ?? [];
        const staff = read?.staff ?? [];
        const pickedTemplateId = templates.some(template => template.id === templateId) ? templateId : read?.templateId;
        const pickedPerson = persons.find(person => person.profileId === profileId);
        const pickedTipStaff = staff.some(member => member.resourceId === tipStaffResourceId) ? tipStaffResourceId : read?.tipStaffResourceId;
        const foundRoom = pickedPerson ? pickedPerson.roomNumber : read?.roomNumber;
        return {templates, persons, staff, pickedTemplateId, pickedPerson, pickedTipStaff, foundRoom};
    };
    const {templates, persons, staff, pickedTemplateId, pickedPerson, pickedTipStaff, foundRoom} = choicesOf(prefill);
    // A tip for the recipient would go on the folio as a plain line - without a service, so with no code for
    // the PMS: their tip service, else the sector's default one, is missing (confirm's pick)
    const tipWithoutService = (read: DigitalSigningPrefill | undefined, templateId?: string, recipientId?: string) => {
        const template = read?.templates?.find(entry => entry.id === templateId);
        if (!read || !template || template.tipEnabled === false) return false;
        const recipient = read.staff?.find(member => member.resourceId === recipientId);
        return !(recipient?.tipServiceId ?? read.defaultTipServiceId);
    };

    const noTemplate = !!prefill && !templates.length;
    const startersEnabled = noTemplate && !!starterKind;
    const startersQuery = useQuery(
        ['digitalSigningTemplates', 'starters'],
        () => DigitalSigningTemplateService.starters(),
        {enabled: startersEnabled, staleTime: Infinity, refetchOnWindowFocus: false}
    );
    const starter = startersQuery.data?.find(entry => entry.kind === starterKind);
    const templateCreate = useMutation(
        (request: DigitalSigningTemplateInstantiateRequest) => DigitalSigningTemplateService.instantiate(request),
        {onSuccess: () => invalidateDigitalSigningTemplateChoices(queryClient)}
    );
    const templateCreateError = useMemo(() => (templateCreate.error ? getErrorMessage(templateCreate.error, '') : ''), [templateCreate.error]);

    const dialogShows = LINK_STATES.includes(state.kind) || (state.kind === 'SIGNED' && stale);
    useEffect(() => {
        if (dialogOpen && !dialogShows) setDialogOpen(false);
    }, [dialogOpen, dialogShows]);

    const run = async <T, >(action: () => Promise<T>, onDone?: (result: T) => void): Promise<T | undefined> => {
        if (busyRef.current) return undefined;
        busyRef.current = true;
        setBusy(true);
        const subjectId = latest?.id;
        let done: {result: T} | undefined;
        try {
            done = {result: await action()};
        } catch (e) {
            if (isDigitalSigningError(e) && e.reason === 'CHANGED') {
                setStaleId(subjectId);
                enqueueSnackbar(t('The folio changed after the guest signed. Please create a new link.'), {variant: 'error'});
            } else {
                enqueueSnackbar((isDigitalSigningError(e) && e.reason === 'FAILED' && e.message) || t('Something went wrong'), {variant: 'error'});
            }
        }
        await query.refetch();
        busyRef.current = false;
        setBusy(false);
        if (done) onDone?.(done.result);
        return done?.result;
    };

    // What Create link sends for a prefill
    const createOptionsOf = (read?: DigitalSigningPrefill): DigitalSigningCreateOptions => {
        const choices = choicesOf(read);
        return {
            roomNumber: room !== undefined ? room.trim() : choices.foundRoom,
            language,
            templateId: choices.pickedTemplateId,
            profileId: choices.pickedPerson?.profileId ?? read?.profileId,
            tipStaffResourceId: choices.pickedTipStaff,
            reservationId: reservation?.id,
        };
    };

    // A create that failed reads the templates again - the lines saved just before may change which
    // apply -; a link that bills another guest has the folio read again
    const settleCreate = (created: DigitalSigningCreateResponse | undefined, billTo: string | undefined) => {
        if (!created && state.kind === 'DRAFT') void prefillQuery.refetch();
        if (created && billTo && billTo !== folio?.profileId) onBillToChanged?.();
    };

    const create = async () => {
        const options = createOptionsOf(prefill);
        const created = await run(async () => {
            if (beforeCreate && !(await beforeCreate())) return undefined;
            return DigitalSigningService.create(folioId!, options);
        });
        settleCreate(created, options.profileId);
        return created;
    };

    /** Saves the draft and issues a signing link with preselected options, opening the dialog to show the QR code. */
    const createRightAway = async () => {
        if (busyRef.current) return;
        if (state.kind !== 'DRAFT' || state.blockedBy || !items || loadFailed) {
            setDialogOpen(true);
            return;
        }
        setCreatingRightAway(true);
        const outcome = await run(async () => {
            // Read with the folio view; a read still running is waited for
            const read = prefillQuery.data ?? (prefillQuery.isFetching ? (await prefillQuery.refetch()).data : undefined);
            const options = createOptionsOf(read);
            const noGuest = !!read && read.persons.length > 1 && !read.persons.some(person => person.profileId === options.profileId);
            // Nobody would receive the tip: the options say so, with the way to the template
            const noRecipient = tipWithoutService(read, options.templateId, options.tipStaffResourceId);
            if (!read || !options.templateId || noGuest || noRecipient) return 'OPTIONS' as const;
            if (beforeCreate && !(await beforeCreate())) return 'UNSAVED' as const;
            return {options, created: await DigitalSigningService.create(folioId!, options)};
        });
        setCreatingRightAway(false);
        if (outcome === 'UNSAVED') return;
        if (outcome !== 'OPTIONS') settleCreate(outcome?.created, outcome?.options.profileId);
        setDialogOpen(true);
    };
    const voidLatest = () => latest && run(() => DigitalSigningService.void(latest.id));
    const confirm = () => latest && run(() => DigitalSigningService.confirm(latest.id), onConfirmed);

    const refresh = async (): Promise<boolean> => {
        const results = await Promise.all([
            query.refetch(),
            prefillEnabled ? prefillQuery.refetch() : undefined,
            startersEnabled ? startersQuery.refetch() : undefined,
        ]);
        return results.every(result => !result?.isError);
    };

    return {
        available,
        state,
        latest,
        token,
        loading,
        loadFailed,
        refetch: () => query.refetch(),
        refresh,
        confirmed: latest?.status === 'CONFIRMED' ? latest : undefined,
        confirmIncomplete: state.kind === 'CONFIRMED' && !!state.incomplete,
        signed: isSignedDigitalSigning(latest?.status) ? latest : undefined,
        pendingTipCents: isDraft && latest?.status === 'SIGNED' && !stale ? latest.tip?.amountCents ?? 0 : 0,
        busy,
        stale,
        templates,
        templateId: pickedTemplateId ?? '',
        setTemplateId,
        noTemplate,
        createTemplate: starter && sectorId ? () => templateCreate.mutate({
            templateId: starter.id,
            language: i18n.language,
            scopedSectorIds: [sectorId],
        }) : undefined,
        creatingTemplate: templateCreate.isLoading,
        createTemplateError: templateCreate.isError ? templateCreateError || t('Something went wrong') : undefined,
        persons,
        profileId: pickedPerson?.profileId ?? prefill?.profileId ?? '',
        setProfileId,
        staff,
        tipStaffResourceId: pickedTipStaff ?? '',
        setTipStaffResourceId,
        // With the options, and beside the QR code of the link just made (the footer's button skips the options)
        tipWithoutService: state.kind === 'LIVE' && latest
            ? tipWithoutService(prefill, latest.templateId, latest.tipStaffResourceId)
            : tipWithoutService(prefill, pickedTemplateId, pickedTipStaff),
        room: room ?? foundRoom ?? '',
        setRoom,
        language: language ?? prefill?.language ?? '',
        languages: prefill?.languages ?? [],
        setLanguage,
        prefilling: prefillQuery.isLoading || startersQuery.isLoading,
        create,
        createRightAway,
        creatingRightAway,
        voidLatest,
        confirm,
        offersLink: available && !loading && LINK_STATES.includes(state.kind),
        dialogOpen,
        openDialog: () => setDialogOpen(true),
        closeDialog: () => setDialogOpen(false),
        detailsOpen,
        showDetails: () => setDetailsOpen(true),
        hideDetails: () => setDetailsOpen(false),
    };
};

export type DigitalSigningFlow = ReturnType<typeof useDigitalSigning>;
